import SettingsIcon from '@material-ui/icons/Settings'
import type { Action } from '@paintscout/ui'
import type { RenderableItem } from '@paintscout/util/builder'

/**
 * The "Save to settings" row action, shared by every table/menu that offers it
 * (the QuoteView tables for line items, AreaMainView for surfaces) so the key,
 * label and icon can't drift between them.
 */
export const saveToSettingsAction: Action = {
  key: 'save-to-settings',
  label: 'Save to settings',
  icon: SettingsIcon
}

/**
 * Whether a QuoteView table row should offer {@link saveToSettingsAction}.
 *
 * Lives here rather than inline at each call site because five tables
 * (`AdditionalTable`, `BidTable`, `OptionsTable`, `OptionsAddedTable`,
 * `PendingTable`) ask the same question, and an inline copy per table lets the
 * gate drift — only some of them would pick up a future change to who may save.
 *
 * `isEditable` defaults to true: most tables only render row actions when the
 * quote is already editable, so they don't pass it. `OptionsAddedTable` renders
 * its actions either way and does.
 *
 * Surfaces (`AreaMainView`) gate differently — office/crew users are excluded
 * there too — so they don't use this.
 */
export function canSaveItemToSettings(args: {
  permissionToEditAccountSettings: boolean
  item: Pick<RenderableItem, 'type'>
  isEditable?: boolean
}): boolean {
  const { permissionToEditAccountSettings, item, isEditable = true } = args

  return !!permissionToEditAccountSettings && isEditable && item?.type === 'line_item'
}
